import { Box, IconButton, Menu } from "@mui/material";
import NotificationDotBadge from "@common/components/notificationDotBadge/NotificationDotBadge";
import React from "react";
import { useLocation } from "react-router";
import { DotLegalButton, DotLegalHeader, DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";
import DotLegalFullScreenSpinner from "../../common/components/dotLegalFullScreenSpinner/DotLegalFullScreenSpinner";
import DotLegalNotificationCard from "../../common/components/dotLegalNotificationCard/DotLegalNotificationCard";
import { notificationIcon } from "../../common/icons";
import { useTranslation } from "../../localization/useTranslation";
import { getNotificationDescriptionCompact, getNotificationHeader, NotificationBaseViewModel } from "../../notification/Notification.types";
import { useNotificaitionMenu } from "./NotificationMenu.hooks";
import NotificationEmptyState from "../../notification/notificationEmptyState/NotificationEmptyState";

export interface INotificationMenuProps {
    isExpanded: boolean;
    showNotificationIcon: boolean;
    isLoading: boolean;
    data: NotificationBaseViewModel[] | undefined;
}

function NotificationMenu(props: INotificationMenuProps) {
    const location = useLocation();

    const { translateString } = useTranslation();
    const { anchorEl, setAnchorEl, open, getNotificationUrl } = useNotificaitionMenu();

    const GetIconAndBadge = () => <NotificationDotBadge show={props.showNotificationIcon}>{notificationIcon}</NotificationDotBadge>;

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    function isActive() {
        let pathname = location.pathname;
        return pathname.indexOf("privacy/notifications") > -1;
    }

    const notificationCardSize = "compact";
    function getMenu() {
        return (
            <Menu
                sx={{
                    "& .MuiMenu-paper": {
                        width: 385,
                        padding: 2,
                    },
                }}
                id="simple-menu"
                anchorEl={anchorEl}
                keepMounted
                transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                }}
                open={open}
                onClose={() => handleClose()}
            >
                {props.isLoading ? (
                    <DotLegalFullScreenSpinner />
                ) : (
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                        {props.data!.length > 0 ? (
                            <React.Fragment>
                                <DotLegalHeader headerStyle="medium">{translateString("notifications")}</DotLegalHeader>

                                {props.data?.map((notification) => {
                                    let header = getNotificationHeader(notification.notificationType, translateString);
                                    return (
                                        <React.Fragment key={notification.id}>
                                            <DotLegalNotificationCard
                                                notificationType={notification.notificationType}
                                                size={notificationCardSize}
                                                header={header}
                                                description={getNotificationDescriptionCompact(notification, translateString)}
                                                taskDate={notification.created}
                                                taskHref={`${getNotificationUrl()}?notificationId=${notification.id}`}
                                                taskHrefText={translateString("readMore")}
                                                isRead={notification.isRead}
                                                onDelete={() => {}}
                                                onReadMoreClick={() => setAnchorEl(null)}
                                                clickable
                                            />
                                        </React.Fragment>
                                    );
                                })}
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        "& .MuiButton-root": { alignSelf: "center", marginTop: 2 },
                                    }}
                                >
                                    <DotLegalButton onClick={() => setAnchorEl(null)} href={getNotificationUrl()} buttonType={"secondary"}>
                                        {translateString("seeAllNotifications")}
                                    </DotLegalButton>
                                </Box>
                            </React.Fragment>
                        ) : (
                            <NotificationEmptyState notificationText={translateString("noNotifications")} isPopOver />
                        )}
                    </Box>
                )}
            </Menu>
        );
    }

    return (
        <>
            <DotLegalTooltip text={translateString("notifications")}>
                <IconButton id={"top-bar-notifications"} onClick={(event: React.MouseEvent<HTMLElement>) => handleClick(event)}>
                    {GetIconAndBadge()}
                </IconButton>
            </DotLegalTooltip>

            {getMenu()}
        </>
    );
}

export default NotificationMenu;
